import Axios from "axios";

// Map to store active AbortController instances per endpoint
const pendingRequests = new Map();

// URLs that should NEVER be aborted (login, logout, auth checks)
const EXCLUDED_PATHS = [
  "login",
  "loginNew",
  "user/login",
  "admin/login",
  "logout",
  "user/logout",
  "refreshToken"
];

const isExcluded = (url = "") => {
  const urlStr = url.toString().toLowerCase();
  return EXCLUDED_PATHS.some((path) => urlStr.includes(path.toLowerCase()));
};

// Request Interceptor: automatically attaches AbortController and cancels previous duplicates
Axios.interceptors.request.use(
  (config) => {
    // If request explicitly opts out or is an excluded path (like login/logout), skip cancellation
    if (config.noCancel || isExcluded(config.url)) {
      return config;
    }

    // Identify request by HTTP Method + base URL path (ignoring query params)
    const urlPath = (config.url || "").toString().split("?")[0];
    const requestKey = `${(config.method || "get").toUpperCase()}:${urlPath}`;

    // If an in-flight request to the same endpoint already exists, abort it
    if (pendingRequests.has(requestKey)) {
      const existingController = pendingRequests.get(requestKey);
      if (existingController && typeof existingController.abort === "function") {
        existingController.abort("Duplicate in-flight request aborted");
      }
      pendingRequests.delete(requestKey);
    }

    // If caller didn't pass their own signal, create a new AbortController
    if (!config.signal) {
      const controller = new AbortController();
      config.signal = controller.signal;
      config._abortKey = requestKey;
      config._abortController = controller;
      pendingRequests.set(requestKey, controller);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor: cleans up completed requests and suppresses abort errors
Axios.interceptors.response.use(
  (response) => {
    const key = response.config?._abortKey;
    if (key && pendingRequests.get(key) === response.config?._abortController) {
      pendingRequests.delete(key);
    }
    return response;
  },
  (error) => {
    const key = error.config?._abortKey;
    if (key && pendingRequests.get(key) === error.config?._abortController) {
      pendingRequests.delete(key);
    }

    // Check if error was caused by abort/cancellation
    const isCancelled =
      Axios.isCancel(error) ||
      error?.code === "ERR_CANCELED" ||
      error?.name === "CanceledError" ||
      error?.message === "canceled" ||
      error?.message === "Duplicate in-flight request aborted" ||
      (typeof error?.message === "string" && error.message.includes("aborted"));

    if (isCancelled) {
      // Silently terminate cancelled requests (returns a non-resolving promise to prevent downstream catch / error toasts)
      return new Promise(() => {});
    }

    return Promise.reject(error);
  }
);

// Function to abort all active pending requests (useful during route/page changes)
export const cancelAllPendingRequests = () => {
  pendingRequests.forEach((controller) => {
    if (controller && typeof controller.abort === "function") {
      controller.abort("Route navigation: all pending requests aborted");
    }
  });
  pendingRequests.clear();
};

export default pendingRequests;
